/**
 * Classifies the outcome of a category refresh — used by BOTH the direct POST /refresh response
 * path (handleRefresh) and the polling GET /:sectorId/category/:categoryPath path
 * (handleTriggerResearch) so the two surfaces cannot silently drift apart, which is exactly what
 * happened before this file existed: handleRefresh already distinguished success / warning /
 * failed, and the poll independently re-implemented an incomplete version of the same logic that
 * (a) treated every active/fresh row as an unconditional success, ignoring researchError, and
 * (b) threw a detected 'failed' status from inside a try/catch whose catch was scoped to
 * TRANSPORT errors, so the catch swallowed it as "keep polling" instead of surfacing it.
 *
 * Pure and side-effect free on purpose: this is exactly the logic codex found untested, because a
 * behavioural test needs a mounted component with mocked fetch/timers, and the shape of the two
 * bugs it exists to prevent had nothing to do with rendering — extracting this to a plain function
 * makes both classification decisions independently, deterministically testable.
 */

export type RefreshOutcome =
  | { kind: 'success' }
  | { kind: 'warning'; message: string }
  | { kind: 'failed'; message: string }
  | { kind: 'unknown'; message: string };

/**
 * Classifies the direct response to POST .../refresh.
 * `okBody` is the FRESH CATEGORY OBJECT (re-fetched after the POST, carrying researchError) — not
 * the raw POST response body, which does not repeat the row's persisted research_error.
 *
 * ⛔ `okBody === null` on a 2xx response means the RELOAD FAILED OR FOUND NO MATCHING ROW, not
 * that the row has nothing wrong with it — a failure to look is not the same as a look that found
 * nothing. Reporting that as 'success' was CR-2 BLOCKING on #4953: the caller cannot distinguish
 * "I checked and it's clean" from "I could not check", which is the same defect shape as a
 * `grep -c || echo NOFILE` reading an inaccessible file as a clean zero. 'unknown' names the gap
 * explicitly instead of silently reporting the optimistic case the code did NOT verify.
 */
export function classifyRefreshResult(
  ok: boolean,
  status: number,
  okBody: { researchError?: string | null } | null,
  errorBody: { error?: string } | null
): RefreshOutcome {
  if (!ok) {
    return { kind: 'failed', message: errorBody?.error || `Request failed with status ${status}` };
  }
  if (!okBody) {
    return {
      kind: 'unknown',
      message: 'The request succeeded, but the result could not be confirmed — check the status below or refresh the page.',
    };
  }
  if (okBody.researchError) {
    return { kind: 'warning', message: okBody.researchError };
  }
  return { kind: 'success' };
}

/**
 * The single outcome -> operator-visible-message mapping, used by ALL THREE terminal report sites
 * (handleRefresh, handleTriggerResearch's fast path, and its polling loop).
 *
 * ⛔⛔ IT IS ONE FUNCTION AND THE SWITCH IS EXHAUSTIVE FOR A MEASURED REASON. Round 3 of this PR
 * widened RefreshOutcome with 'unknown' and updated only TWO of the three hand-written if/else
 * chains. The third — the polling loop — ended in a catch-all `else` that reported "Research
 * Complete", so an 'unknown' outcome (the request succeeded but we could NOT confirm what
 * happened) would have been shown to the operator as a clean success. That is precisely the
 * defect class this whole PR exists to remove, reintroduced by the fix for it, and `tsc` could
 * not see it because A TRAILING `else` IS NOT AN EXHAUSTIVENESS CHECK — it silently absorbs every
 * kind added later.
 * ⇒ The `never` assignment below is what makes adding a fifth kind a COMPILE ERROR at the one
 * place that must handle it, instead of a silent success message at whichever call sites the
 * author forgot. The throw is an unreachable-by-construction backstop that fails loud rather than
 * defaulting to the optimistic message.
 */
export interface OutcomeToast {
  title: string;
  description: string;
  variant: 'default' | 'destructive';
}

export function toastForOutcome(outcome: RefreshOutcome, successDescription: string): OutcomeToast {
  switch (outcome.kind) {
    case 'success':
      return { title: 'Research Complete', description: successDescription, variant: 'default' };
    case 'warning':
      return { title: 'Research Completed With Warnings', description: outcome.message, variant: 'destructive' };
    case 'failed':
      return { title: 'Research Failed', description: outcome.message, variant: 'destructive' };
    case 'unknown':
      return { title: 'Research Status Unknown', description: outcome.message, variant: 'default' };
    default: {
      const unhandled: never = outcome;
      throw new Error(`unhandled refresh outcome: ${JSON.stringify(unhandled)}`);
    }
  }
}

/**
 * Why a single poll attempt failed to produce an answer. An HTTP failure has a status; a transport
 * failure (fetch rejected, or a 2xx whose JSON would not parse) has only a message — so the two
 * cannot share one shape without one of them lying about what was observed.
 */
export type PollFailure =
  | { kind: 'http'; status: number; body: string }
  | { kind: 'transport'; message: string }
  // ⛔ THE SERVER SUCCESSFULLY ANSWERED "THERE IS NO SUCH ROW". That is neither an error nor
  // progress, and it is the one shape that defeats BOTH other controls: a failure counter sees no
  // failure (the request succeeded) and an exhaustive switch sees a well-formed value. The GET
  // route returns { success: true, data: null } for a missing (sector, path) — which is what a
  // refresh that died before persisting the category leaves behind. Counted, never silently
  // treated as "not yet".
  | { kind: 'absent' };

/** Keeps a server error body from filling the toast when it is an HTML error page. */
function truncate(text: string, max = 300): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export interface PollFailureState {
  consecutiveFailures: number;
  lastFailure: PollFailure | null;
}

/** A poll that reached the server and parsed resets BOTH fields — see the contract on
 *  pollExhaustionToast: only a genuinely-answered last attempt licenses the progress message. */
export const NO_POLL_FAILURES: PollFailureState = { consecutiveFailures: 0, lastFailure: null };

export const MAX_CONSECUTIVE_POLL_FAILURES = 3;

/**
 * ⛔⛔ ONE COUNTER FOR EVERY WAY A POLL CAN FAIL, BECAUSE THE DEFECT WAS EXACTLY THAT THERE WERE
 * TWO. The HTTP-error branch and the transport-error catch were written separately, and only the
 * HTTP one incremented and retained — so a run in which every attempt THREW consumed all 24 polls
 * and reported "Research In Progress", a claim of progress from a loop that never got an answer.
 * ⇒ Same shape as the trailing-`else` earlier in this PR: THE BUG LIVED IN THE SECOND HAND-WRITTEN
 * COPY OF A DECISION, not in either copy's logic. Routing both through here means a third failure
 * mode cannot be added that silently does not count.
 */
export function recordPollFailure(
  state: PollFailureState,
  failure: PollFailure
): { state: PollFailureState; stop: boolean } {
  // An expired or rejected credential cannot be repaired by asking again, so it stops immediately
  // rather than burning the remaining attempts to reach the same answer.
  const nonRetryable = failure.kind === 'http' && (failure.status === 401 || failure.status === 403);
  const consecutiveFailures = state.consecutiveFailures + 1;
  return {
    state: { consecutiveFailures, lastFailure: failure },
    stop: nonRetryable || consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES,
  };
}

/**
 * What the operator sees when the poll loop runs out of attempts.
 *
 * ⛔⛔ THIS IS THE THIRD INSTANCE OF THIS PR'S OWN DEFECT CLASS INSIDE THIS PR, and the one the
 * exhaustive switch structurally could not catch. Both review legs landed on it independently at
 * the same line: poll transport and JSON-decode errors were LOGGED AND NOTHING ELSE — not counted,
 * not retained — so a loop in which EVERY attempt threw still ended on "Research In Progress",
 * telling the operator a run was progressing when the loop had never once successfully asked.
 * 🔑 AN EXHAUSTIVENESS CHECK CANNOT DEFEND A PATH THAT NEVER CONSTRUCTS A VALUE. `toastForOutcome`
 * protects every outcome that REACHES it; this path produced no outcome at all, it threw and the
 * loop continued. The defence for a never-constructed value is a COUNTER plus a retained cause,
 * which is what this function reads.
 *
 * The binding predicate, stated as the function's contract: a null `lastFailure` means the most
 * recent attempt genuinely reached the server and was told the work is not yet terminal — only
 * then may this say "in progress". Any retained failure means the question was never answered, and
 * that must never be rendered as progress.
 */
export function pollExhaustionToast(lastFailure: PollFailure | null, categoryName: string): OutcomeToast {
  if (!lastFailure) {
    return {
      title: 'Research In Progress',
      description: `${categoryName} research is taking longer than expected. Refresh the page to check status.`,
      variant: 'default',
    };
  }
  let detail: string;
  switch (lastFailure.kind) {
    case 'http':
      detail = `status ${lastFailure.status}${lastFailure.body ? `: ${truncate(lastFailure.body)}` : ''}`;
      break;
    case 'transport':
      detail = `no response from the server (${truncate(lastFailure.message)})`;
      break;
    case 'absent':
      detail = 'the server reports no record for this category, so the refresh did not persist one';
      break;
    default: {
      const unhandled: never = lastFailure;
      throw new Error(`unhandled poll failure: ${JSON.stringify(unhandled)}`);
    }
  }
  return {
    title: 'Could Not Check Research Status',
    description:
      `Checking on ${categoryName} failed — ${detail}. The research may still be running server-side; `
      + `refresh the page to see where it got to.`,
    variant: 'destructive',
  };
}

export type PollOutcome = RefreshOutcome | { kind: 'continue' };

/**
 * Classifies one poll of GET .../category/:categoryPath — given a row that EXISTS.
 *
 * ⛔ IT NO LONGER ACCEPTS null, AND THAT IS THE FIX. It used to map null to 'continue' with the
 * comment "a transport failure — the caller's job to retry". That contract was true when it was
 * written and became false when the loop was restructured: transport failures now never reach
 * here (they are caught, and `body` stays null before this is called), so the ONLY thing that
 * could arrive as null was `{ success: true, data: null }` — the server explicitly answering
 * "there is no such row". Every such poll reset the failure counter, so exhaustion reported
 * "Research In Progress" while the server had repeatedly confirmed there was nothing there.
 * 🔑 A FUNCTION'S DOCUMENTED CONTRACT CAN BE FALSIFIED BY A CHANGE TO ITS CALLER WITHOUT EITHER
 * ONE LOOKING WRONG. Absence is now decided in exactly one place — the loop — as a PollFailure.
 */
export function classifyPollResult(category: {
  status?: string;
  freshness?: { status?: string; error?: string };
  researchError?: string | null;
}): PollOutcome {
  const status = category.freshness?.status || category.status;

  if (status === 'active' || status === 'fresh') {
    if (category.researchError) {
      return { kind: 'warning', message: category.researchError };
    }
    return { kind: 'success' };
  }

  if (status === 'failed') {
    return {
      kind: 'failed',
      message: category.researchError || category.freshness?.error || 'Research failed',
    };
  }

  // 'researching' / 'pending' / anything else not yet terminal.
  return { kind: 'continue' };
}

/**
 * ⛔⛔ THE KEY A REFRESH ACTUALLY WROTE UNDER — TAKEN FROM THE SERVER, NEVER DERIVED HERE.
 *
 * The refresh route normalises the submitted path to a `writeKey` before persisting, so a bare
 * category name ("Bicycle Fenders") can be stored under a resolved canonical path. Confirming with
 * the SUBMITTED path then looks up a key that does not exist, gets `data: null`, and reports
 * "Research Status Unknown" for a completely clean write.
 * ⇒ That is the MIRROR of this PR's defect — reporting failure for success — and it was introduced
 * BY the confirmation step added to stop success being reported for failure. It is also the worse
 * direction operationally: a false "unknown" teaches the operator to ignore the signal.
 *
 * ⛔ AND THE OBVIOUS FIX IS THE WRONG ONE. Category-key normalisation is this codebase's richest
 * source of false zeros — FOUR distinct normalisations across THREE character sets, a count that is
 * itself a filed finding. A client that predicts `writeKey` would be a FIFTH, in another language,
 * diverging from a set that is already inconsistent with itself. ⇒ The POST response already
 * carries the row it wrote; its `categoryPath` IS the stored key. Ask, do not derive.
 *
 * The fallback to the submitted path is defensive only — `_formatCharacteristics` always emits
 * `categoryPath` on a successful write — and it degrades to the HONEST outcome: if the key is
 * absent and the lookup misses, classification reports 'unknown', which is true.
 */
export function storedKeyFrom(refreshPayload: unknown, submittedPath: string): string {
  const data = (refreshPayload as { data?: { categoryPath?: unknown } } | null)?.data;
  return typeof data?.categoryPath === 'string' && data.categoryPath ? data.categoryPath : submittedPath;
}
